/**
 * Symbol search (top bar) — Phase 6a-2 commit 7 of the decomposition
 * (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, A36, v2.6/R22).
 * Body moved VERBATIM from static/index.html's parse-time IIFE; the only
 * edits are the approved ctx.* substitutions.
 *
 * Searches the Fyers master (NSE+BSE) and opens any symbol as a chart via
 * the generic openOptionChart path (history + periodic sync; live candle
 * streaming stays index-only for now, same as option charts).
 *
 * MUST be invoked at the page's original parse-time position: the A36→A38
 * order is load-bearing — initTopBarLayout relocates the injected widget by
 * id (put(row,'symbol-search-wrap')) — and the document/window listener
 * registration order below is behaviour (blueprint rule 4).
 *
 * The injected context is FROZEN at three entries (v2.6/R22) — additions
 * only via an evidenced blueprint amendment.
 */

export interface SymbolSearchContext {
  /** Page-owned auth header builder (A5). */
  authHeaders(extra?: Record<string, string>): Record<string, string>;
  /** Page-owned 401/banner path — the search 401 path (R22). */
  handleAuthFailure(resp: { status: number }): void;
  /** A9 page function (never extracted): opens the picked symbol as a chart. */
  openOptionChart(symbol: string, strike: string, optType: string, labelOverride: string): void;
}

export function initSymbolSearch(ctx: SymbolSearchContext): void {
  const topBar = document.getElementById('top-bar');
  if (!topBar) return;
  const wrap = document.createElement('div');
  wrap.id = 'symbol-search-wrap';
  wrap.style.cssText = 'position:relative;display:inline-block;flex-shrink:0;';
  const input = document.createElement('input');
  input.id = 'symbol-search-input';
  input.type = 'text';
  input.placeholder = '🔍 Search symbol…';
  const results = document.createElement('div');
  // Body-level + position:fixed (mirrors #ism-color-popover / #tf-dropdown):
  // #symbol-search-wrap lives inside #tb-row, which has overflow:auto (toolbar
  // reorg), so an absolutely-positioned child that extends below the row was
  // CLIPPED to the row height ("thin black strip", 2026-07-29). Rendering the
  // results at document.body means no toolbar overflow can clip them; left/top
  // are computed from the input's rect on each show/scroll/resize.
  results.style.cssText = 'display:none;position:fixed;width:320px;max-height:320px;'
    + 'overflow-y:auto;z-index:9999;background:#171A1E;color:#E7EBF0;border:1px solid #2b323b;'
    + 'border-radius:6px;font:12px -apple-system,sans-serif;box-shadow:0 6px 24px rgba(0,0,0,0.5);';
  wrap.appendChild(input);
  document.body.appendChild(results);   // NOT wrap — escape the toolbar overflow
  topBar.appendChild(wrap);

  let debounce: ReturnType<typeof setTimeout> | null = null;
  function positionResults(): void {
    const r = input.getBoundingClientRect();
    results.style.left = r.left + 'px';
    results.style.top = (r.bottom + 2) + 'px';
  }
  function hideResults(): void { results.style.display = 'none'; }
  input.addEventListener('input', () => {
    const q = input.value.trim();
    if (debounce) clearTimeout(debounce);
    if (q.length < 2) { hideResults(); return; }
    debounce = setTimeout(async () => {
      try {
        const resp = await fetch('/api/symbol-search?q=' + encodeURIComponent(q), { headers: ctx.authHeaders() });
        if (!resp.ok) { ctx.handleAuthFailure(resp); hideResults(); return; }
        const data = await resp.json();
        const items = data.results || [];
        if (input.value.trim() !== q) return; // stale response
        results.innerHTML = '';
        if (!items.length) { hideResults(); return; }
        for (const it of items) {
          const row = document.createElement('div');
          row.style.cssText = 'padding:6px 10px;cursor:pointer;border-bottom:1px solid #23282f;display:flex;justify-content:space-between;gap:8px;';
          const name = document.createElement('b');
          name.textContent = it.symbol.split(':').pop();
          const desc = document.createElement('span');
          desc.style.cssText = 'color:#787b86;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
          desc.textContent = it.description;
          row.appendChild(name); row.appendChild(desc);
          row.addEventListener('mouseenter', () => { row.style.background = '#23282f'; });
          row.addEventListener('mouseleave', () => { row.style.background = ''; });
          row.addEventListener('click', () => {
            hideResults();
            input.value = '';
            ctx.openOptionChart(it.symbol, '', '', it.description + ' (' + it.symbol + ')');
          });
          results.appendChild(row);
        }
        positionResults();   // fixed-position under the input, re-read each show
        results.style.display = 'block';
      } catch (e) { hideResults(); }
    }, 250);
  });
  // Dismiss on outside-click — results is now a body child, so a click inside
  // it (a row, the scrollbar) must NOT count as "outside" and dismiss.
  document.addEventListener('click', (e) => {
    if (!wrap.contains(e.target as Node) && !results.contains(e.target as Node)) hideResults();
  });
  input.addEventListener('keydown', (e) => { if (e.key === 'Escape') { hideResults(); input.blur(); } });
  // Keep the fixed dropdown pinned under the input while it's open (the toolbar
  // row can scroll horizontally; the window can resize).
  const reposIfOpen = () => { if (results.style.display !== 'none') positionResults(); };
  window.addEventListener('scroll', reposIfOpen, true);   // capture: catch the #tb-row scroll too
  window.addEventListener('resize', reposIfOpen);
}
